/**
 * The per-move assistant (fix F-D / D2): a POST after every ply of an
 * assisted ("Play and Learn") game, ported from `getAssistedGameMoveAnalysis`
 * (PlayChess index.js L1119-1142, called from the `player_turn_response`
 * handler at L2643-2656) — and the live eval bar's own mode switch (D3),
 * since both start and stop on the exact same edges.
 *
 * `liveGameStore.subscribe` — NOT a render subscription (UX spec §6a: this
 * screen's hooks pay no React render for a store write; `rail/useSounds.ts`
 * is the same shape, multiple named sub-handlers combined into one `apply`).
 * `@/Hooks/*` is the eager-graph landmine (ARCHITECTURE §13 landmine 1), so
 * the REST mutation hook is `require()`'d inside this hook's own body — the
 * same shape `rail/tournament/useTournamentDetails.ts` uses for `@/Api/*`.
 *
 * FIX L / L2 (2026-10-06): `onSuccess` no longer gates on `hint_response`
 * being present (the owner's "feedback is given against the generated
 * hint" — a flagged move with no text still needs its glyph); it gates on
 * `assistStore.pending` instead, keyed by `move_index` and matched against
 * the response's own `fen` — a response for a ply this hook never asked
 * about (a stale table after a rematch, or any unknown ply) is dropped
 * rather than keying a stale hint onto the new game.
 *
 * FIX Z1.1 (2026-10-06, owner report: "the symbols of inaccuracy mistake and
 * blunder are not being shown"): the fen match is now TOLERANT
 * (`samePosition` — the first two FEN fields only), not `===` — castling
 * rights / en-passant / halfmove / fullmove differences are normalisation
 * noise, never a different position, and the strict compare was dropping
 * real responses. A 4xx/5xx from the POST, and every dropped response, now
 * log a dev-only `console.warn` instead of vanishing silently.
 *
 * NO AUTO-OPEN (owner, 2026-10-07): "don't auto open hint card — only when
 * user clicks it, then only open." This superseded an earlier review fix
 * (a guard against a late response for an older ply yanking an open NEWER
 * card backward) — moot once nothing auto-opens at all. `onSuccess` still
 * stores every hint it is given (`setHint`, both sides, every ply, flagged
 * or not); the card itself now opens ONLY through the player's own press
 * (`rail/live/MoveList`'s `openHint` -> `assistStore.open`) — this hook
 * never calls `open` (or the now-removed `openLatest`) itself. The chip
 * that makes a hint reachable (`rail/live/MoveList/View.tsx`) is no longer
 * flagged-only either, so a move with nothing wrong still has somewhere to
 * click through to its hint text.
 */
import {useCallback, useEffect, useRef} from 'react';

import {liveGameStore} from '@/Store/liveGameStore';
import type {LiveGameState} from '@/Store/liveGameStore';

import {useAssistStore} from '../rail/assist/assistStore';
import {buildHint, samePosition} from '../rail/assist/hintText';
import {useAnalysis} from '../rail/analysis/analysisStore';

declare const __DEV__: boolean;

/** fix Z1.1: dev-only — the same guard `stage/renderCount.ts` uses. */
const devWarn = (...args: any[]): void => {
  if (typeof __DEV__ !== 'undefined' && !__DEV__) {
    return;
  }
  console.warn(...args);
};

export function useAssistEffects(): void {
  const useAssistedGameMoveAnalysisApi =
    require('@/Hooks/AssistedGameMoveAnalysisApi/useAssistedGameMoveAnalysisApi')
      .default ??
    require('@/Hooks/AssistedGameMoveAnalysisApi/useAssistedGameMoveAnalysisApi');

  const onSuccess = useCallback((data: any) => {
    if (!data) {
      return;
    }
    const pending = useAssistStore.getState().pending[data.move_index];
    if (!pending || !samePosition(pending.fen, data.fen)) {
      devWarn('[v2 assist] hint dropped', {
        reason: pending ? 'position-mismatch' : 'no-pending',
        move_index: data.move_index,
        pending: pending?.fen,
        got: data.fen,
      });
      return;
    }
    const isBlackTurnFirst = liveGameStore.getState().game.isBlackTurnFirst;
    // owner, 2026-10-07: no auto-open — `setHint` alone, the card opens
    // only through the player's own press (`rail/live/MoveList`)
    useAssistStore
      .getState()
      .setHint(
        data.move_index,
        buildHint(data, isBlackTurnFirst, pending.mine),
      );
  }, []);

  // fix Z1.1: never silent — the mutation hook's own onError gets no
  // variables back (only the error), so `move_index` comes from the last
  // POST this hook itself sent, tracked alongside `mutateRef` below. This
  // is BEST-EFFORT: if two POSTs are in flight at once (routine once a fast
  // bot keeps up with both of my moves) it names whichever was sent LAST,
  // not necessarily the one that actually errored.
  const lastMoveIndexRef = useRef<number | null>(null);
  const onError = useCallback((err: any) => {
    devWarn('[v2 assist] move analysis failed', {
      move_index: lastMoveIndexRef.current ?? undefined,
      status: err?.response?.status,
      message: err?.response?.data ?? err?.message,
    });
  }, []);

  const {mutate: postMoveAnalysis} = useAssistedGameMoveAnalysisApi(
    onSuccess,
    onError,
  );
  // the mutation's own identity is not guaranteed stable across renders; the
  // subscription below is registered once, so it reaches it through a ref
  const mutateRef = useRef(postMoveAnalysis);
  mutateRef.current = postMoveAnalysis;

  useEffect(() => {
    /**
     * D3: the live eval bar follows two edges — a NEW assisted game reaching
     * 'playing' switches `analysisStore` into 'assist'; leaving it (the game
     * ends, or the screen drops back to an un-assisted state) switches it
     * back to 'off'. `rail/Rail/model.ts`'s own 'starting'-phase effect is
     * what resets the store for the table BEFORE this one — raising 'assist'
     * is this hook's alone, same as the spec asks.
     *
     * fix L / L1: `setMode('off')`, NOT `reset()` — the chips the assisted
     * game's queue already settled survive for the post-game read; the
     * 'starting'-phase guard above resets everything for the NEXT game.
     */
    const onAssistMode = (state: LiveGameState) => {
      const mode = useAnalysis.getState().mode;
      const assisting = state.game.assisted && state.game.phase === 'playing';
      if (assisting) {
        if (mode === 'off') {
          useAnalysis.getState().setMode('assist');
        }
      } else if (mode === 'assist') {
        useAnalysis.getState().setMode('off');
      }
    };

    let lastTableId = liveGameStore.getState().game.tableId;
    let lastMoveCount = liveGameStore.getState().game.moves.length;

    /**
     * The per-move POST itself (PlayChess L2643-2656): `userid`/`move`/`fen`
     * (the position AFTER the move)/`color` (MY colour, not the mover's —
     * the old screen's own field) /`last_fen` (before it) /`player` ('me' when
     * I was the one who just moved, else 'opponent') /`uuid` (the table id)
     * /`move_index` (1-based, `moves.length`). `lastMoveCount` is reseeded on
     * every table change: this hook mounts once for the screen's whole
     * lifetime, never per game, so a rematch's shorter move list must not be
     * compared against the PREVIOUS game's count.
     *
     * fix L / L2: `markPending` fires immediately BEFORE the POST, with the
     * SAME `player` value the payload carries — `onSuccess` matches its
     * response back against this (keyed by `move_index`, checked against the
     * `fen`), and the owner's own last word keeps this call for every ply of
     * BOTH sides.
     */
    const onMoveAnalysis = (state: LiveGameState) => {
      if (state.game.tableId !== lastTableId) {
        lastTableId = state.game.tableId;
        lastMoveCount = state.game.moves.length;
      }
      if (
        !state.game.assisted ||
        state.game.phase !== 'playing' ||
        state.game.moves.length <= lastMoveCount
      ) {
        return;
      }
      lastMoveCount = state.game.moves.length;

      const moveIndex = state.game.moves.length;
      const player =
        state.game.turnUserId === state.session.me ? 'opponent' : 'me';
      useAssistStore
        .getState()
        .markPending(moveIndex, {fen: state.game.fen, mine: player === 'me'});
      lastMoveIndexRef.current = moveIndex;
      mutateRef.current({
        userid: state.session.me,
        move: state.game.moves[moveIndex - 1],
        fen: state.game.fen,
        color: state.game.myColor === 'white' ? 'white' : 'black',
        last_fen: state.game.previousFen ?? '',
        player,
        uuid: String(state.game.tableId ?? ''),
        move_index: moveIndex,
      });
    };

    const apply = (state: LiveGameState) => {
      onAssistMode(state);
      onMoveAnalysis(state);
    };

    const unsubscribe = liveGameStore.subscribe(apply);
    return unsubscribe;
  }, []);
}

export default useAssistEffects;
